
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { FormBirthdayDatePicker } from '@/components/ui/form-birthday-date-picker';
import { Form } from '@/components/ui/form';

// Schema for student form validation
const studentSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  dateOfBirth: z.string().optional(),
  gender: z.string().optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal(''))
});

export type StudentFormValues = z.infer<typeof studentSchema>;

interface AddStudentFormProps {
  familyId: string;
  familyName?: string;
  onSubmit: (data: StudentFormValues, continueToDetails?: boolean) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const AddStudentForm: React.FC<AddStudentFormProps> = ({
  familyName,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const form = useForm<StudentFormValues>({
    resolver: zodResolver(studentSchema),
    defaultValues: {
      firstName: '',
      lastName: familyName || '',
      dateOfBirth: '',
      gender: '',
      notes: ''
    }
  });

  const { register, handleSubmit, setValue, formState: { errors }, reset } = form;

  // Pre-fill last name with family name when the component mounts or family name changes
  useEffect(() => {
    if (familyName) {
      setValue('lastName', familyName);
    }
  }, [familyName, setValue]);

  const handleFormSubmit = (data: StudentFormValues) => {
    onSubmit(data, false); // false = don't continue to details
  };

  const handleFormSubmitAndContinue = (data: StudentFormValues) => {
    onSubmit(data, true); // true = continue to details
  };

  const handleCancel = () => {
    reset({
      firstName: '',
      lastName: familyName || '',
      dateOfBirth: '',
      gender: '',
      notes: ''
    });
    onCancel();
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name</Label>
          <Input id="firstName" {...register('firstName')} />
          {errors.firstName && (
            <p className="text-sm text-destructive">{errors.firstName.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name</Label>
          <Input id="lastName" {...register('lastName')} />
          {errors.lastName && (
            <p className="text-sm text-destructive">{errors.lastName.message}</p>
          )}
        </div>
      </div>

      <FormBirthdayDatePicker
        name="dateOfBirth"
        label="Date of Birth"
        placeholder="Select date of birth"
      />

      <div className="space-y-2">
        <Label htmlFor="gender">Gender</Label>
        <Select onValueChange={(value) => setValue('gender', value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select gender" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="female">Female</SelectItem>
            <SelectItem value="male">Male</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        {errors.gender && (
          <p className="text-sm text-destructive">{errors.gender.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea id="notes" {...register('notes')} />
        {errors.notes && (
          <p className="text-sm text-destructive">{errors.notes.message}</p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Adding...' : 'Add Student'}
        </Button>
        <Button 
          type="button" 
          onClick={handleSubmit(handleFormSubmitAndContinue)}
          disabled={isLoading}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {isLoading ? 'Adding...' : 'Add & Edit Full Details'}
        </Button>
      </div>
      </form>
    </Form>
  );
};

export default AddStudentForm;
